import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import styled, { keyframes, useTheme } from 'styled-components';
import { lgTile } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassPointer, glassShadow } from '../components/TipGlass';
import { fetchMySales, peekCache, monthsParams, MySalesData, MySalesDeal, MySalesTrendPoint, MySalesRefundDispute, MySalesLeadsPerf, fetchMySalesReport, MySalesReport } from '../services/api';
import DownloadMenu, { DownloadChoice } from '../components/DownloadMenu'; // [MYSALES-REPORT 2026-10-28]
import ReportPeopleDialog from '../components/ReportPeopleDialog'; // [MYSALES-REPORT-PICK 2026-10-28]
import { downloadMySalesReport, reportPeople } from '../utils/mySalesReport'; // [MYSALES-REPORT 2026-10-28]
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide'; // [VIEW-SLIDE 2026-10-08]
import { useRealtime } from '../hooks/useRealtime';
import { Column, SortConfig } from '../types';
import SearchBar from '../components/SearchBar';
import DataTable from '../components/DataTable';
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE-ALL 2026-09-29] keys the remembered column widths
import { TableSkeleton, SkeletonBox } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import GlassSelect from '../components/GlassSelect';
import ScaleToFit from '../components/ScaleToFit'; // [SPLIT-VIEW 2026-07-15] KPI row now lives in half width
import { effectiveCurrentMonth } from '../utils/effectiveMonth';

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONEY_WHOLE_FMT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });   // [OVERALL-BREAKDOWN 2026-09-29]
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
const MONTH_SHORT_FMT = new Intl.DateTimeFormat('en-US', { month: 'short' });

import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { usePresence } from '../hooks/usePresence'; // [CHART TOOLTIP MOTION 2026-09-17]
import { durationMs } from '../styles/motion'; // [CHART TOOLTIP MOTION 2026-09-17]
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] tips placed in page px

/**
 * My Sales — live HubSpot Sales-Pipeline deals for the month (by close date) plus
 * the sales-team refund/dispute KPIs from our DB. Mirrors the My Inventory layout:
 * a frozen snapshot strip + toolbar on top, the deal table scrolling below.
 */

/* ── helpers ── */

// [EFFECTIVE-MONTH 2026-07-01] App-wide effective current month (12:30 PM IST
// rollover on the 1st) so the default month lines up with the rest of the app.
function currentMonth(): string {
  return effectiveCurrentMonth();
}

function formatMonthFull(m: string): string {
  const [y, mo] = m.split('-');
  return MONTH_LONG_YEAR_FMT.format(new Date(Number(y), Number(mo) - 1));
}

// [NET-IN-BANK 2026-09-16] Sign before the $ ("-$200.00", not "$-200.00") — Net Sales and the
// Overall Sales In Bank / ATV can go negative in a month where refunds outweigh sales.
function fmtCurrency(n: number): string {
  const v = Number(n) || 0;
  return `${Math.round(v * 100) < 0 ? '-' : ''}$${MONEY_FMT.format(Math.abs(v))}`; // no "-$0.00" from float dust
}

// [OVERALL-BREAKDOWN 2026-09-29, user] Whole dollars for the Overall Sales view ("in the overall section,
// don't show the decimal values"). Same sign rule as fmtCurrency: "-$200", never "-$0". The magnitude is
// rounded first so the sign and the printed number can never disagree (-0.5 → "-$1", -0.4 → "$0").
function fmtCurrencyWhole(n: number): string {
  const v = Number(n) || 0;
  const r = Math.round(Math.abs(v));
  return `${v < 0 && r > 0 ? '-' : ''}$${MONEY_WHOLE_FMT.format(r)}`;
}

// [LEADS-PERF 2026-07-17] Compact month label for the leads table: '2026-01' -> 'Jan-26'.
function fmtMonthYY(m: string): string {
  const [y, mo] = m.split('-');
  const name = MONTH_SHORT_FMT.format(new Date(Number(y), Number(mo) - 1));
  // [DATE-STYLE 2026-09-29, user] "Jan-26" → "Jan 2026" (one date style across the app).
  return `${name} ${y}`;
}
const fmtPct = (n: number) => `${(Number(n) || 0).toFixed(2)}%`;
const fmtInt = (n: number) => (Number(n) || 0).toLocaleString('en-US');

const money = (v: any) => fmtCurrency(Number(v) || 0);

// HubSpot stores the recurring payment type as 'Reoccurring' (its internal,
// misspelled value). Show it as 'Recurring' in the UI; everything else as-is.
function paymentTypeLabel(v: string | null | undefined): string {
  return v === 'Reoccurring' ? 'Recurring' : (v || '—');
}

// Trend-chart line colours (work on both themes): recurring = green, one-time = blue.
const TREND_REC_COLOR = '#0dc198';
const TREND_OT_COLOR = '#38bdf8';
// [CHART REFUNDS 2026-09-17, user] Third line: Refunds / Disputes — the same red as that KPI tile (dark).
const TREND_RD_COLOR = '#f87171';
// [SALES-PIA 2026-09-18, user] Fourth line: Sales PIA — amber, the one hue clearly apart from the
// green / blue / red already on the chart, and legible on both themes.
const TREND_PIA_COLOR = '#f59e0b';

/* ── layout (sticky-freeze scaffold, like Refunds / My Inventory) ── */

/* [SPLIT-VIEW 2026-07-15] The split is confined to the TOP band only: the chart +
   KPI strip sit left of a divider, SplitRight is reserved for a new table. The
   toolbar and the deals table below it stay FULL WIDTH, so Container is the same
   plain column it always was (sticky-freeze scaffold: only ScrollContainer scrolls). */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* Phones: SplitTop stacks, so the page is taller than the viewport — let the
     whole thing scroll rather than freezing a top band that leaves no room. */
  @media (max-width: 768px) {
    overflow-y: auto;
  }
`;

/* The split band. Height is driven by its content (chart + KPIs); SplitRight
   stretches to match it, so the divider spans exactly that band and stops. */
const SplitTop = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  min-width: 0;

  /* Phones: a 50/50 split would crush both halves, so stack them. */
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/* LEFT of the divider — the Last 6 Months chart + the Sales Snapshot KPI strip.
   Carries the padding FrozenTop used to apply to them. */
const SplitLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  /* min-width: 0 lets a flex child shrink below its content width, so the chart
     fits the half instead of forcing the pane wider. */
  min-width: 0;
  padding: 14px 16px 0;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    padding: 14px 12px 0; /* [PHONE-LAYOUT 2026-10-22] 12px side gutter on phones (was 16) */
  }
`;

/* RIGHT of the divider — reserved for the table to be built here. 50/50 with
   SplitLeft; change the `flex` basis on both to re-weight the split. */
const SplitRight = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  padding: 14px 16px 12px;
  border-left: 1px solid ${(p) => p.theme.line};

  @media (max-width: 768px) {
    flex: 0 0 auto;
    border-left: none;
    border-top: 1px solid ${(p) => p.theme.line};
    min-height: 150px;
    padding: 14px 12px 12px; /* [PHONE-LAYOUT 2026-10-22] 12px side gutter on phones (was 16) */
  }
`;

/* Placeholder until the real table lands. No dock clearance needed — the dock
   floats over the full-width table below, not over this band. */
const RightPlaceholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: ${(p) => p.theme.muted};
  font-size: 13.5px;
  font-weight: 500;
  padding: 20px;
  text-align: center;
`;

/* [LEADS-PERF 2026-07-17] Right-pane leads-performance table (fed by data.leadsPerf).
   Title = the selected rep (or "All Sales Rep"); 6 completed months + a Total row. */
const PerfWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;
/* [SPLIT-HEADINGS 2026-07-17] Section heading for each half of the split band:
   "Sales" (left, above the chart) and "Leads to Sales" (right, centred above the table). */
const PaneHeading = styled.div<{ $center?: boolean; $withToggle?: boolean }>`
  position: relative;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: ${(p) => p.theme.text};
  padding-bottom: 9px;
  margin-bottom: 11px;
  /* Full-width hairline separator between the heading and the content below (both panes). */
  border-bottom: 1px solid ${(p) => p.theme.line};
  ${(p) => (p.$center ? 'text-align: center;' : '')}

  /* [BUTTON-STYLE 2026-09-30] The one switch-pill size is ~38px tall, twice this heading's text line:
     pinned on it, it cut into "Leads to Sales | …" (1366px) and sat on the separator. Desktop only
     (phones already put the toggle on its own line): both panes' text lines become 38px, so their
     separators still line up, and the toggle's pane centres its title in the space LEFT of the toggle. */
  /* [MYSALES-REPORT 2026-10-28, user] "on the right side the toggle button, on the left side the download button, and
     the text in the middle": three columns, the title centred in the space between the two (equal gaps). Phones: the
     title on its own line, then Download on the left and the toggle on the right (HeadDownload / HeadTitle / ToggleWrap). */
  @media (min-width: 769px) {
    line-height: 38px;
    ${(p) => (p.$withToggle ? 'display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; column-gap: 16px;' : '')}
  }
  @media (max-width: 768px) {
    ${(p) => (p.$withToggle ? "display: grid; grid-template-columns: auto minmax(0, 1fr); grid-template-areas: 'title title' 'dl tog'; align-items: center; row-gap: 8px;" : '')}
  }
`;
/* [HEADING 2026-07-17] The scope clause after the "|" in a pane heading — brand green
   (theme-aware), matching the app's accent (the Recurring line / "Last 6 Months" label).
   The title and the "|" stay the heading's default white; only this scope text is green. */
const HeadingScope = styled.span`
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  font-weight: 600;
`;

/* [TOGGLE 2026-07-17] Leads↔Overall segmented toggle, at the far right of the right-pane heading.
   [MYSALES-REPORT 2026-10-28, user] No longer pinned with position absolute: it is the heading's right column (laptop)
   and sits at the right end of the line under the title (phones). The heading's text line is 38px, the pill's height,
   so the separator's height and its line-up with the left pane are unchanged. */
const ToggleWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    grid-area: tog;
  }
`;
/* [MYSALES-REPORT 2026-10-28, user] The Download button: the heading's left column (laptop), the left end of the line
   under the title (phones). */
const HeadDownload = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    grid-area: dl;
  }
`;
/* [MYSALES-REPORT 2026-10-28, user] The title, centred between the Download button and the toggle (laptop); its own line
   on phones. A very long rep name ends in "…" on the laptop rather than pushing into the buttons. */
const HeadTitle = styled.div`
  min-width: 0;

  @media (min-width: 769px) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 768px) {
    grid-area: title;
  }
`;
/* [BUTTON-STYLE 2026-09-30] Track and option sizes come from styles/buttons (pillTrack / pillSegment),
   the one switch-pill size; the knob insets follow the 3px track padding. */
const PerfToggle = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  ${pillTrack}
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => p.theme.line};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border-color: transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;
/* The sliding brand-green highlight that animates between the two options. */
const PerfToggleKnob = styled.div<{ $right: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc(50% - 3px);
  border-radius: 999px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  transform: translateX(${(p) => (p.$right ? '100%' : '0%')});
  transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1);
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
`;
const PerfToggleOpt = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  min-width: 84px;
  border: none;
  background: transparent;
  cursor: pointer;
  ${pillSegment}
  color: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? '#06281d' : '#ffffff')
    : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;
/* Narrow half-pane fallback: let the 6 money/number columns scroll rather than crush. */
const PerfScroll = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { height: 0; }
  /* [FILL 2026-07-17] Desktop only: fill the pane vertically so the table can stretch its
     rows down to the left side's height (Total row lines up with the KPI card's bottom).
     On phones the panes stack, so heights are natural — no stretch. */
  @media (min-width: 769px) {
    flex: 1 1 auto;
    min-height: 0;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: the table swipes inside this box without dragging the page or the browser's
     back gesture along at its ends. */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;
/* [IN-BANK-NOTE 2026-09-16, user] Two lines under the table saying where In Bank comes from in
   each view. Small and muted so the table stays the focus; the view name leads each line. */
const PerfNotes = styled.div`
  margin-top: 8px;
  padding: 0 4px;
  font-size: 11px;
  line-height: 1.45;
  text-align: center;
  color: ${(p) => p.theme.muted};

  b {
    font-weight: 700;
    color: ${(p) => p.theme.text};
  }
`;
// Static text, so PerfSkeleton renders it too — the table keeps the same height when data lands.
// [VIEW-SLIDE 2026-10-08] Now rendered ONCE under the sliding table (skeleton or data) — it is the same in both views,
// so it stays put while the toggle slides.
const InBankNotes: React.FC = () => (
  <PerfNotes>
    <div><b>Leads to Sales:</b> In Bank is the value of sales from the leads assigned in that month, even if the sale closed in a different month.</div>
    {/* [OVERALL-BREAKDOWN 2026-09-29, user] Names the parts, which are now columns of that view. */}
    <div><b>Overall Sales:</b> Net In-Bank = Recurring + One-Time + Sales PIA − Refund / Dispute + Disputes Won, for sales closed in that month.</div>
  </PerfNotes>
);
const PerfTable = styled.table<{ $wide?: boolean; $minWidth?: number }>`
  width: 100%;
  /* [ALIGN 2026-07-17] Even, fixed-width columns so the header, subtitle and every row
     share one grid — no more ragged auto-sized columns under the centred heading. On a
     narrow pane the table keeps this min-width and scrolls inside PerfScroll (no clipping). */
  min-width: ${(p) => p.$minWidth ?? 540}px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  /* [FILL 2026-07-17] Desktop: fill PerfScroll's height so the rows distribute the extra
     space (cells are middle-aligned) and the Total row ends level with the left side. */
  @media (min-width: 769px) {
    height: 100%;
  }

  th, td {
    padding: 6px 10px;
    white-space: nowrap;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* [OVERALL-BREAKDOWN 2026-09-29, user] ONE header height for both views: the Overall view's two-line
     headers (47px measured). Leads to Sales gets the same row, so the toggle never changes the table's
     height ("the toggle switch should not move the page"). */
  thead tr { height: 48px; }

  /* [THEME 2026-07-17] Match the app's DataTable headers exactly: brand-green (mint in dark)
     uppercase on the slate pill band, so this table reads like every other table in the app. */
  thead th {
    line-height: 1.3;   /* [OVERALL-BREAKDOWN 2026-09-29] same line box in both views */
    background: ${(p) => p.theme.pill};
    padding: 9px 10px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-bottom: 1px solid ${(p) => p.theme.line};
  }

  tbody td { color: ${(p) => p.theme.text}; }
  tbody td:first-child {
    font-weight: 600;
    color: ${(p) => p.theme.muted};
  }
  /* Hairline between data rows for a clean, ledger-like read. */
  tbody tr + tr td { border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')}; }

  tfoot td {
    font-weight: 700;
    color: ${(p) => p.theme.text};
    border-top: 1px solid ${(p) => p.theme.line};
    padding-top: 10px;
  }

  /* [OVERALL-BREAKDOWN 2026-09-29, user] The Overall view carries the Net In-Bank parts, up to 11
     columns in the same half pane: side padding drops to 6px and a header may wrap between its word
     groups (PerfHeadChunk), so every number keeps its full width. Column widths come from a colgroup. */
  ${(p) => (p.$wide ? `
    th, td { padding-left: 6px; padding-right: 6px; }
    thead th { padding-left: 6px; padding-right: 6px; white-space: normal; line-height: 1.3; }
  ` : '')}

  /* [PHONE-LAYOUT 2026-10-22] Phones: the table already swipes inside PerfScroll, so no column has to squeeze. Each
     column takes at least its content (auto layout, the min-width and the colgroup still spread the rest), so no
     header or figure is cut with an ellipsis ("CONVERSION%", "Apr 2026" were). Data 13px, rows 8px top and bottom
     like the app's other phone tables. */
  @media (max-width: 768px) {
    table-layout: auto;
    font-size: 13px;
    th, td { overflow: visible; text-overflow: clip; }
    tbody td { padding-top: 8px; padding-bottom: 8px; }
  }
`;
/* [OVERALL-BREAKDOWN 2026-09-29, user] One unbreakable word group of a wrapping header: a header
   breaks only between groups, never inside "One-Time" / "In-Bank" at the hyphen. */
const PerfHeadChunk = styled.span`
  white-space: nowrap;
`;
/* [OVERALL-BREAKDOWN 2026-09-29, user] Refund / Dispute red and Disputes Won green: the same colours
   as those two Sales Snapshot tiles (KpiValue). */
const PerfAmt = styled.span<{ $tone: 'red' | 'green' }>`
  color: ${(p) => (p.$tone === 'red'
    ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
    : (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d'))};
`;
/* [OVERALL-BREAKDOWN 2026-09-29, user] The Net In-Bank parts, shown after Conversion% in the Overall
   Sales view. Recurring + One-Time always; the other three only for a rep with an amount in them in
   one of the 6 months (user: "only for the sales rep who have values in these columns"). */
type PerfPartKey = 'recurring' | 'oneTime' | 'pia' | 'refundsDisputes' | 'disputesWon';
const PERF_PARTS: ReadonlyArray<{ key: PerfPartKey; head: string[]; always: boolean; tone?: 'red' | 'green' }> = [
  { key: 'recurring', head: ['Recurring', 'New Sales'], always: true },
  { key: 'oneTime', head: ['One-Time', 'New Sales'], always: true },
  { key: 'pia', head: ['Sales PIA'], always: false },
  { key: 'refundsDisputes', head: ['Refund /', 'Dispute'], always: false, tone: 'red' },
  { key: 'disputesWon', head: ['Disputes', 'Won'], always: false, tone: 'green' },
];
/* The narrowest each Overall-view column may be, 12px side padding included, measured with the real
   font (Plus Jakarta Sans) in Chrome: "May-26" 48px; an All Sales Rep leads Total ("11,069", bold) 42px;
   CONVERSION% is one 93px word; a money column must hold its longest header word (RECURRING, 70px),
   which is also room for a seven-figure whole-dollar bold Total ("$1,234,568", 70px). Columns share the
   pane in these proportions, and below their sum the table scrolls sideways inside PerfScroll rather
   than cutting a number off. */
const PERF_W = { month: 62, leads: 56, sales: 52, conversion: 108, money: 82 };

/* [SKELETON 2026-07-17] Right-pane loading state. Reuses the REAL table's styled
   components (same fixed columns, padding, hairlines) with shimmer bars in each cell,
   so the placeholder is the exact shape of the table that replaces it — it fills in
   with no layout jump. Mirrors the ChartSkeleton / TableSkeleton pattern. */
const PERF_COLS = 6;
const PERF_ROWS = 6;
// [OVERALL-BREAKDOWN 2026-09-29, user] `cols` = 8 in the Overall view (its fewest columns: the two
// always-shown parts are added), so the placeholder is closer to the table that replaces it.
// [VIEW-SLIDE 2026-10-08] The table part only: it sits in a sliding pane; PerfWrap (aria-busy) and InBankNotes are
// rendered around both panes by the page.
const PerfSkeleton: React.FC<{ cols?: number }> = ({ cols = PERF_COLS }) => (
    <PerfScroll>
      <PerfTable>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}><SkeletonBox $w="58%" $h="9px" $radius="5px" $align="center" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: PERF_ROWS }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><SkeletonBox $w={c === 0 ? '62%' : '50%'} $h="12px" $align="center" /></td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c}><SkeletonBox $w={c === 0 ? '52%' : '56%'} $h="13px" $radius="6px" $align="center" /></td>
            ))}
          </tr>
        </tfoot>
      </PerfTable>
    </PerfScroll>
);

/* [VIEW-SLIDE 2026-10-08, user] Leads to Sales <-> Overall Sales SLIDES like the app's other view toggles
   (components/ViewSlide): going right (-> Overall Sales) the Leads to Sales table leaves to the LEFT while Overall Sales
   comes in from the RIGHT; back reverses it. Only the table moves — the heading, the pill and the two In Bank lines stay
   put. Desktop: the table fills the pane's height ([FILL 2026-07-17]), so the viewport and BOTH panes carry that flex
   chain on, and a parked pane is pinned top-to-bottom so its table keeps the same height while it slides. Phones: plain
   blocks with natural heights, as before. Both views come from the one loaded payload — the toggle loads nothing. */
const PERF_VIEWS = ['leads', 'overall'] as const;

/* [MYSALES-REPORT 2026-10-28, user] The Download button (left end of the Leads to Sales heading): this table for every
   active sales rep the user may see (admin / senior = all, a rep = himself), as one Excel file (two tabs) or one PDF
   (utils/mySalesReport). */
const DOWNLOAD_CHOICES: DownloadChoice[] = [
  { key: 'xlsx', label: 'Excel file', hint: 'Two tabs: Leads to Sales, Overall Sales', tag: '.xlsx', icon: 'fa-solid fa-file-excel', tone: 'green' },
  { key: 'pdf', label: 'PDF file', hint: 'Each sales rep: the name, then the tables', tag: '.pdf', icon: 'fa-solid fa-file-pdf', tone: 'red' },
];
/** "Apr – Sep 2026" (or "Nov 2026 – Apr 2027") for the Download menu's heading. */
function fmtPeriodShort(first: string, last: string): string {
  const [fy, fm] = first.split('-').map(Number);
  const [ly, lm] = last.split('-').map(Number);
  const a = MONTH_SHORT_FMT.format(new Date(fy, fm - 1));
  const b = MONTH_SHORT_FMT.format(new Date(ly, lm - 1));
  return fy === ly ? `${a} – ${b} ${ly}` : `${a} ${fy} – ${b} ${ly}`;
}
const PerfViewport = styled(SlideViewport)`
  @media (min-width: 769px) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;

    & > * {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      bottom: 0;
    }
  }
`;

/* Full width, below the split band: the toolbar freezes above the scrolling table.
   No top padding — the KPI strip's own margin-bottom supplies the gap. */
const FrozenTop = styled.div`
  flex-shrink: 0;
  padding: 0 16px;
  background: ${(p) => p.theme.surface};

  @media (max-width: 768px) {
    padding: 0 12px; /* [PHONE-LAYOUT 2026-10-22] 12px side gutter on phones (was 16) */
  }
`;

/* [PHONE-LAYOUT 2026-10-22] Phones only (rendered only when isMobile): the page head on top. The title, then the
   Month + Sales Rep pickers right under it: they set every part of the page (chart, KPIs, Leads to Sales, deals),
   and in the toolbar they sat a full screen down, under the Leads to Sales table. */
const PhoneHead = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 12px 0;
`;

/* ── snapshot KPI strip ── */

const Snapshot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.45) 100%)'};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)')};
`;

const SnapHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long rep name + month drops under the title as one line, not wrapped
     inside itself beside it. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    row-gap: 2px;
  }
`;

const SnapTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
`;

const SnapMeta = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

/* [SILENT-PARTIAL FIX 2026-08-15] Banner shown when the HubSpot deal fetch failed (amber =
   the figures are unknown) or served a stale-but-successful cache (muted = still real data,
   just older). Sits directly under the Snapshot heading so it is impossible to read the KPI
   strip without seeing it. Amber, not red: nothing is broken in OUR data — a third party is
   rate-limiting us and the DB-backed tiles beside it are still exact. */
const DealsWarn = styled.div<{ $tone: 'warn' | 'info' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  color: ${(p) => (p.$tone === 'warn'
    ? (p.theme.colorScheme === 'dark' ? '#fbbf24' : '#92400e')
    : p.theme.muted)};
  background: ${(p) => (p.$tone === 'warn'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.14)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)'))};
  border: 1px solid ${(p) => (p.$tone === 'warn'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.28)' : 'rgba(146,64,14,0.22)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)'))};
`;

/* [SILENT-PARTIAL FIX 2026-08-15] Stands in for the 6-month trend when the deal fetch failed.
   A zero-filled trend draws a FLAT LINE ALONG $0, which is a confident factual claim that the
   reps sold nothing for six months — the single most misleading thing on the page during a
   HubSpot outage. Say nothing rather than say something false. */
const ChartUnavailable = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 96px;
  padding: 8px 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

/* One row, never wraps — every KPI shares the width (KPI-single-row rule).
   [MOBILE 2026-07-07] Phones: the 5 KPI tiles can't stay readable squeezed into one
   screen, so the row becomes a horizontal-scroll carousel showing 2 tiles per screen
   (a sliver of the next peeking as the affordance) with scroll-snap settling each tile
   to the left edge — 2-up keeps the labels from truncating. Same family as the My
   Inventory Performance Snapshot strip (KpiGridMobile). Desktop is byte-for-byte
   unchanged (flex tiles share the width). */
/* [SPLIT-VIEW 2026-07-15] The KPI row moved into the LEFT half of the split, so
   5 tiles now share ~620px instead of ~1300px. At that width the labels
   ("REFUNDS / DISPUTES") ellipsis away and long values overflow their tile. Same
   problem the Inventory Performance Snapshot solved: wrap the row in <ScaleToFit>
   so it scales down UNIFORMLY as one readable row instead of truncating. 150px is
   the natural tile width (borrowed from InventoryPage's proven KPI_CELL_W). */
const KPI_CELL_W = 150;
const KPI_GAP = 10;
const kpiRowMinWidth = (n: number) => n * KPI_CELL_W + (n - 1) * KPI_GAP;

const KpiRow = styled.div`
  display: flex;
  gap: ${KPI_GAP}px;

  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    /* No visible scrollbar — the peeking next tile is the scroll affordance. */
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    overscroll-behavior-x: contain; /* [PHONE-LAYOUT 2026-10-22] swipe stays in the row */
  }
`;

const Kpi = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 9px 13px;
  border-radius: 10px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.6)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')};

  @media (max-width: 768px) {
    /* Two tiles per viewport (one 10px gap) minus a peek so the 3rd tile hints the
       row scrolls; flex-shrink:0 keeps each tile at full readable width so labels like
       "Recurring Sales" / "Refunds / Disputes" don't truncate. */
    flex: 0 0 calc((100% - 10px) / 2 - 14px);
    scroll-snap-align: start;
    /* [PHONE-LAYOUT 2026-10-22] The label may now take two lines: label on top, figure at the bottom, so the
       figures of side-by-side tiles stay level. min-width auto = a tile is never narrower than its figure
       (a long amount widens its tile instead of running over the next one). */
    min-width: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const KpiLabel = styled.div`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px (9.5px was too small to read) and wraps to a second line instead of
     an ellipsis ("REFUNDS / DISPUTES" does not fit half the screen in one line). */
  @media (max-width: 768px) {
    font-size: 11px;
    line-height: 1.25;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
`;

const KpiValue = styled.div<{ $tone?: 'green' | 'red' | 'strong' }>`
  margin-top: 3px;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
  color: ${(p) =>
    p.$tone === 'red'
      ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
      : p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : p.theme.text};

  /* [PHONE-LAYOUT 2026-10-22] Phones: an amount never breaks over two lines (its tile widens instead, see Kpi). */
  @media (max-width: 768px) {
    white-space: nowrap;
  }
`;

/* ── Refunds / Disputes KPI hover breakdown ──────────────────────────────────
   The Refunds/Disputes KPI is a rolled-up deduction; hovering the tile reveals
   the individual rows behind the number (website · sales rep · amount) so a lead
   can see exactly what the figure is made of. Portaled to <body> so the frozen
   snapshot strip's overflow can't clip it; anchored BELOW the tile (the strip
   sits high on the page — no room for a tall card above). */
const tipIn = keyframes`
  from { opacity: 0; transform: translate(-50%, 4px); }
  to   { opacity: 1; transform: translate(-50%, 10px); }
`;

const Tooltip = styled.div`
  position: fixed;
  z-index: 1200;
  /* [ALL-ROWS 2026-07-07] Interactive so the cursor can move onto the card and scroll
     the list (see TipList) — the tile→card gap is bridged by a hide delay below. */
  pointer-events: auto;
  min-width: 244px;
  max-width: 320px;
  padding: 11px 14px;
  /* [ONE GLASS RECIPE 2026-09-01, user] "make the same tooltip design, all over the application."
     Surface comes from the shared recipe in components/TipGlass — the same one the chart
     tooltips, the title capsule and the sidebar tooltip use. Only the SURFACE is shared: this
     card keeps its own size, padding, notch and pointer-events (it is interactive and scrollable,
     unlike a plain capsule). It previously ran its own near-opaque fill at blur 24px / radius 12. */
  border-radius: ${GLASS_RADIUS}px;
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
  animation: ${tipIn} 0.16s cubic-bezier(0.16, 1, 0.3, 1) both;

  /* Notch pointing up at the tile. */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: -5px;
    width: 11px;
    height: 11px;
    transform: translateX(-50%) rotate(45deg);
    background: ${(p) => glassPointer(p.theme.colorScheme === 'dark')};
    border-left: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
    border-top: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: centred on the screen under the tile (pinned to the tile centre it ran off the
     screen edge for a tile near a side), never wider than the screen; the notch would point at the wrong place. */
  @media (max-width: 768px) {
    left: 50% !important;
    width: min(320px, calc(100vw - 16px));
    min-width: 0;
    max-width: none;
    &::after { display: none; }
  }
`;

const TipHeader = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 9px;
  padding-bottom: 7px;
  border-bottom: 1px solid ${(p) => p.theme.line};

  @media (max-width: 768px) {
    font-size: 11px; /* [PHONE-LAYOUT 2026-10-22] readable on phones (was 9.5px) */
  }
`;

const TipItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  & + & { margin-top: 8px; }
`;

const TipItemLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TipSite = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long website wraps (no ellipsis) — there is no hover title to read it. */
  @media (max-width: 768px) {
    max-width: none;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const TipRep = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px and wraps instead of an ellipsis. */
  @media (max-width: 768px) {
    font-size: 11px;
    max-width: none;
    white-space: normal;
  }
`;

const TipAmt = styled.span<{ $tone?: 'red' | 'green'; $strong?: boolean }>`
  flex: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$strong ? 800 : 700)};
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

// [ALL-ROWS 2026-07-07] The scrollable list region — every row renders (no "+N more"
// cap); when a month has more rows than fit, this scrolls internally while the header +
// Total stay pinned. The card is interactive so the cursor can enter and scroll it.
const TipList = styled.div`
  /* ~7 rows visible (a sliver of the 8th hints "scroll for more"); the rest scroll. */
  max-height: min(50vh / var(--app-zoom, 1), 256px); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin-right: -6px;   /* let the scrollbar sit at the edge without cramping amounts */
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(22,25,23,0.22)')} transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.24)' : 'rgba(22,25,23,0.20)')};
    border-radius: 3px;
  }
`;

const TipDivider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.line};
  margin: 9px 0 8px;
`;

const TipTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const TipTotalLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.muted};
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-22] was 9.5px */
`;

// A KPI tile that reveals a portaled breakdown card on hover when it has underlying
// rows. Reused by Refunds/Disputes (red, deduction — shows Refund vs Dispute per row)
// and Disputes Won (green — all rows are won disputes, so the per-row type is hidden).
// [ALL-ROWS 2026-07-07] The card lists EVERY row (no cap) and is interactive: the list
// scrolls when long, and a short hide delay lets the cursor travel onto the card and
// scroll it without the card vanishing (the card's own mouseenter cancels the hide).
const BreakdownKpi: React.FC<{
  label: string;
  tone: 'red' | 'green';
  loading: boolean;
  value: number | undefined;
  items: MySalesRefundDispute[];
  countLabel: (n: number) => string;
  showType?: boolean;
}> = ({ label, tone, loading, value, items, countLabel, showType = true }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const hasData = !loading && items.length > 0;

  const hideTimer = useRef<number | null>(null);
  const cancelHide = () => { if (hideTimer.current != null) { window.clearTimeout(hideTimer.current); hideTimer.current = null; } };
  const scheduleHide = () => { cancelHide(); hideTimer.current = window.setTimeout(() => setTip(null), 160); };
  useEffect(() => cancelHide, []);   // clear the pending timer on unmount

  const show = () => {
    cancelHide();
    const el = ref.current;
    if (!el || !hasData) return;
    const r = pageRect(el); // [APP-FIT 2026-10-26] page px, like the tip's left / top
    setTip({ x: r.left + r.width / 2, y: r.bottom });
  };

  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  return (
    <Kpi ref={ref} onMouseEnter={show} onMouseLeave={scheduleHide} style={{ cursor: 'default' }}>
      <KpiLabel>{label}</KpiLabel>
      <KpiValue $tone={tone}>
        {loading
          ? <SkeletonBox $w="96px" $h="17px" $radius="6px" />
          : value !== undefined ? fmtCurrency(value) : '—'}
      </KpiValue>
      {tip && createPortal(
        <Tooltip style={{ left: tip.x, top: tip.y }} onMouseEnter={cancelHide} onMouseLeave={() => setTip(null)}>
          <TipHeader>{countLabel(items.length)}</TipHeader>
          <TipList>
            {items.map((it, i) => (
              <TipItem key={`${it.website}-${it.salesRep}-${i}`}>
                <TipItemLeft>
                  <TipSite>{it.website || '—'}</TipSite>
                  <TipRep>{(it.salesRep || 'Unassigned')}{showType ? ` · ${it.type}` : ''}</TipRep>
                </TipItemLeft>
                <TipAmt $tone={tone}>{fmtCurrency(it.amount)}</TipAmt>
              </TipItem>
            ))}
          </TipList>
          <TipDivider />
          <TipTotalRow>
            <TipTotalLabel>Total</TipTotalLabel>
            <TipAmt $tone={tone} $strong>{fmtCurrency(total)}</TipAmt>
          </TipTotalRow>
        </Tooltip>,
        document.body
      )}
    </Kpi>
  );
};

/* ── 6-month trend chart (2 lines: recurring + one-time) ── */

const TrendStrip = styled.div`
  padding: 10px 14px 8px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.45) 100%)'};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
`;

const TrendHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TrendTitleWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
`;

const TrendTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
`;

const TrendMeta = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the four keys did not fit one row and were squeezed into their own labels
     ("One-" / "Time", "Sales" / "PIA"); now whole keys wrap onto a second row. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    column-gap: 14px;
    row-gap: 6px;
  }
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  @media (max-width: 768px) { white-space: nowrap; } /* [PHONE-LAYOUT 2026-10-22] a key never splits */
`;

const Swatch = styled.span<{ $color: string }>`
  width: 14px;
  height: 3px;
  border-radius: 2px;
  background: ${(p) => p.$color};
`;

const ChartArea = styled.div`
  position: relative;
  height: 84px;
  margin-top: 10px;
`;

/* [CHART TOOLTIP MOTION 2026-09-17, user] "make the tooltip appearance smooth … not looking
   professional". Everything below animates transform/opacity only (styles/motion.ts rules), on the
   app's own tokens — fast 180ms, entrance cubic-bezier(0.16, 1, 0.3, 1), standard
   cubic-bezier(0.4, 0, 0.2, 1), exit cubic-bezier(0.4, 0, 1, 1) — and switches off under
   prefers-reduced-motion. The dot grows by scale (was a width/height jump). */
const Dot = styled.div<{ $color: string; $active?: boolean }>`
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: 1.5px solid ${(p) => p.theme.surface};
  transform: translate(-50%, -50%) scale(${(p) => (p.$active ? 1.4 : 1)});
  pointer-events: none;
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

/* [CHART TOOLTIP 2026-09-17, user] "it will show single tooltip and which will show the complete data
   for the month". The per-point value labels are gone (user: "Remove all labels") — they overlapped
   once the third line arrived. Each month owns an invisible full-height hover band; hovering it
   highlights that month (guide line + bigger dots) and opens ONE tooltip with every figure. */
const HoverBand = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  cursor: default;
`;

/* The guide line lives in a full-width track that is TRANSLATED to the month (a translateX % of a
   full-width box is a % of the chart), so moving between months glides instead of jumping, and it
   fades rather than popping in and out. */
const HoverGuideTrack = styled.div<{ $shown: boolean; $glide: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  /* Same rule as the tooltip: jump into place when newly shown, glide after that. */
  transition: ${(p) => (p.$glide
    ? 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1), opacity 180ms cubic-bezier(0.4, 0, 0.2, 1)'
    : 'opacity 180ms cubic-bezier(0.4, 0, 0.2, 1)')};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

/* [MY-SALES PHONE CHART SCROLL 2026-09-23, user] Clips the track above to the chart. Translated by up
   to 95% of its own width, the track reached far past the chart's right edge, and on phones (where
   ChartScroll scrolls sideways) that invisible overhang counted as scrollable width: 280px of blank
   past September on load, 532px after tapping September. The guide line itself always sits inside
   the chart, so clipping hides nothing. */
const HoverGuideClip = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const HoverGuide = styled.div`
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 1px;
  margin-left: -0.5px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.16)')};
`;

/* Outer = WHERE the tooltip is (a transform, so it can glide from month to month once shown — the
   glide is off for the very first placement, or it would fly in from the corner). Inner = HOW it
   appears: fades in while rising 6px and settling from 98% scale; fades out slightly faster. */
const ChartTipPos = styled.div<{ $glide: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1200;
  pointer-events: none;
  will-change: transform;
  transition: ${(p) => (p.$glide ? 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none')};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const ChartTip = styled.div<{ $shown: boolean }>`
  min-width: 212px;
  padding: 10px 13px;
  border-radius: ${GLASS_RADIUS}px;
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
  transform-origin: 50% 100%;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transform: ${(p) => (p.$shown ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.98)')};
  transition: ${(p) => (p.$shown
    ? 'opacity 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.16, 1, 0.3, 1)'
    : 'opacity 120ms cubic-bezier(0.4, 0, 1, 1), transform 120ms cubic-bezier(0.4, 0, 1, 1)')};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const ChartTipMonth = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: ${(p) => p.theme.text};
  margin-bottom: 8px;
`;

const ChartTipRow = styled.div<{ $strong?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  font-size: 12px;
  font-weight: ${(p) => (p.$strong ? 800 : 600)};
  color: ${(p) => (p.$strong ? p.theme.text : p.theme.muted)};
  & + & { margin-top: 5px; }
`;

const ChartTipName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
`;

const ChartTipSwatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex: none;
`;

const ChartTipAmt = styled.span<{ $tone?: 'red' | 'green' }>`
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === 'red'
      ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
      : p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : p.theme.text};
`;

const ChartTipDivider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.line};
  margin: 8px 0 7px;
`;

const MonthRow = styled.div`
  position: relative;
  height: 14px;
  margin-top: 4px;
`;

const MonthLabel = styled.span`
  position: absolute;
  transform: translateX(-50%);
  font-size: 10.5px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-22] was 10.5px; 100px apart in the 560px chart */
`;

// Thin 3-line chart: recurring + one-time (+ refunds/disputes) over the last 6 months. Each line is an
// SVG that stretches to fill (preserveAspectRatio:none, non-scaling stroke); the
// dots + month labels are HTML positioned by percentage so they stay round/legible.
/** [CHART TOOLTIP MOTION 2026-09-17] How long the cursor must rest on the chart before the tooltip
 *  first opens — long enough that sweeping the cursor across the chart does not flash it, short
 *  enough not to feel laggy. Moving between months while it is open is immediate (it glides). */
const CHART_TIP_OPEN_DELAY_MS = 90;

const TrendChart: React.FC<{ data: MySalesTrendPoint[] }> = ({ data }) => {
  // [GRAPHITE-EMERALD 2026-09-28] recurring series: jade in light, emerald in dark.
  const recColor = useTheme().colorScheme === 'dark' ? '#46bf82' : TREND_REC_COLOR;
  // [CHART TOOLTIP 2026-09-17] Hooks before any early return.
  const [active, setActive] = useState<number | null>(null);
  // [CHART TOOLTIP MOTION 2026-09-17] false for the first placement of a newly opened tooltip (so it
  // appears in place), true one frame later (so later month changes glide).
  const [glide, setGlide] = useState(false);
  const areaRef = useRef<HTMLDivElement>(null);
  const posRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const lastGuideX = useRef(50);
  const n = data.length;
  const padX = 5;
  const x = (i: number) => (n > 1 ? padX + (i / (n - 1)) * (100 - 2 * padX) : 50);
  const cur = active != null && active < n ? data[active] : null;
  // Keeps the tooltip mounted through its fade-out, still showing the month it was showing.
  const pres = usePresence(cur, durationMs.fast);
  const shown = pres.value;
  const shownIdx = shown ? data.findIndex((d) => d.month === shown.month) : -1;

  useEffect(() => () => { if (openTimer.current != null) window.clearTimeout(openTimer.current); }, []);
  useEffect(() => { if (!pres.mounted) setGlide(false); }, [pres.mounted]);

  // Place the tooltip above the hovered month (below it if there is no room), clamped on screen.
  // Written as a transform on the outer box so a month change can glide on the compositor.
  useLayoutEffect(() => {
    const area = areaRef.current;
    const pos = posRef.current;
    if (!pres.mounted || shownIdx < 0 || !area || !pos) return undefined;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom), like offsetWidth and the translate below.
    const a = pageRect(area);
    const view = pageViewport();
    const tw = pos.offsetWidth;
    const th = pos.offsetHeight;
    const GAP = 10;
    const MARGIN = 8;
    const cx = a.left + (a.width * x(shownIdx)) / 100;
    const left = Math.max(MARGIN, Math.min(cx - tw / 2, view.width - tw - MARGIN));
    let top = a.top - th - GAP;
    if (top < MARGIN) top = Math.min(a.bottom + GAP, view.height - th - MARGIN);
    pos.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
    if (!glide) {
      const raf = requestAnimationFrame(() => setGlide(true));
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  });

  // First entry waits CHART_TIP_OPEN_DELAY_MS; once the tooltip is up (or still fading out), a new
  // month takes over at once and the tooltip glides to it.
  const enterMonth = (i: number) => {
    if (openTimer.current != null) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    if (active != null || pres.mounted) { setActive(i); return; }
    openTimer.current = window.setTimeout(() => { openTimer.current = null; setActive(i); }, CHART_TIP_OPEN_DELAY_MS);
  };
  const leaveChart = () => {
    if (openTimer.current != null) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    setActive(null);
  };
  if (active != null && active < n) lastGuideX.current = x(active);

  if (!n) return <ChartArea />;
  // [CHART REFUNDS 2026-09-17, user] Third line = Refunds / Disputes per month (the tile's rows).
  // Drawn only when the server sent it for every month — a partial line would read as zeros.
  const hasRd = data.every((d) => d.refundsDisputes != null);
  const rd = (d: MySalesTrendPoint) => Number(d.refundsDisputes) || 0;
  // [SALES-PIA 2026-09-18, user] Fourth line = Sales PIA per month (the tile's deals). Same rule as
  // the refunds line: drawn only when the server sent it for every month, so an older build shows
  // no line instead of a flat $0 that would read as "no PIA sold".
  const hasPia = data.every((d) => d.pia != null);
  const pia = (d: MySalesTrendPoint) => Number(d.pia) || 0;
  const max = Math.max(1, ...data.flatMap((d) => [d.recurring, d.oneTime, hasRd ? rd(d) : 0, hasPia ? pia(d) : 0]));
  // [CHART TOOLTIP 2026-09-17] Labels are gone, so less headroom is needed above and below the lines.
  const topPad = 12, botPad = 10;
  const y = (v: number) => topPad + (1 - v / max) * (100 - topPad - botPad);
  const pts = (sel: (d: MySalesTrendPoint) => number) => data.map((d, i) => `${x(i)},${y(sel(d))}`).join(' ');
  // Left edge of month i's hover band (i = n → the right edge of the chart).
  const bandLeft = (i: number) => (i <= 0 ? 0 : i >= n ? 100 : (x(i - 1) + x(i)) / 2);
  // Tooltip figures — the same formulas as the snapshot tiles, so a month here equals the tiles
  // when that month is selected: Total New Sales = Recurring + One-Time; Net Sales = Total −
  // Refunds / Disputes + Disputes Won. Unknown refund data → "—" rather than an undeducted net.
  // Read from `shown` (not `cur`) so the fading-out tooltip keeps its figures.
  const curTotal = shown ? shown.recurring + shown.oneTime : 0;
  const curRd = shown && shown.refundsDisputes != null ? shown.refundsDisputes : null;
  const curWon = shown && shown.disputesWon != null ? shown.disputesWon : null;
  // [SALES-PIA 2026-09-18, user] Net Sales = Total New Sales + Sales PIA + Disputes Won − Refunds /
  // Disputes, the same formula as the tile. PIA is its OWN term — never inside Total New Sales.
  const curPia = shown && shown.pia != null ? Number(shown.pia) : null;
  const curNet = shown && curRd != null && curWon != null && curPia != null
    ? curTotal + curPia - curRd + curWon
    : null;
  return (
    <>
      <ChartArea ref={areaRef} onMouseLeave={leaveChart}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
          <polyline points={pts((d) => d.recurring)} fill="none" stroke={recColor} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={pts((d) => d.oneTime)} fill="none" stroke={TREND_OT_COLOR} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          {hasPia && (
            <polyline points={pts(pia)} fill="none" stroke={TREND_PIA_COLOR} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          )}
          {hasRd && (
            <polyline points={pts(rd)} fill="none" stroke={TREND_RD_COLOR} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          )}
        </svg>
        <HoverGuideClip>
          <HoverGuideTrack
            $shown={active != null}
            $glide={glide}
            style={{ transform: `translateX(${lastGuideX.current}%)` }}
          >
            <HoverGuide />
          </HoverGuideTrack>
        </HoverGuideClip>
        {data.map((d, i) => (
          <React.Fragment key={d.month}>
            <Dot $color={recColor} $active={active === i} style={{ left: `${x(i)}%`, top: `${y(d.recurring)}%` }} />
            <Dot $color={TREND_OT_COLOR} $active={active === i} style={{ left: `${x(i)}%`, top: `${y(d.oneTime)}%` }} />
            {hasPia && <Dot $color={TREND_PIA_COLOR} $active={active === i} style={{ left: `${x(i)}%`, top: `${y(pia(d))}%` }} />}
            {hasRd && <Dot $color={TREND_RD_COLOR} $active={active === i} style={{ left: `${x(i)}%`, top: `${y(rd(d))}%` }} />}
            {/* The month's hover band runs from halfway to the previous point to halfway to the
                next, so the bands tile the chart with no overlap and no gap. A tap opens it too
                (phones fire mouseenter + click on a tap, so click only opens — never toggles). */}
            <HoverBand
              style={{ left: `${bandLeft(i)}%`, width: `${bandLeft(i + 1) - bandLeft(i)}%` }}
              onMouseEnter={() => enterMonth(i)}
              onClick={() => setActive(i)}
              aria-label={`${formatMonthFull(d.month)} figures`}
            />
          </React.Fragment>
        ))}
      </ChartArea>
      {pres.mounted && shown && createPortal(
        <ChartTipPos ref={posRef} $glide={glide}>
        <ChartTip role="tooltip" $shown={pres.entered && !pres.closing}>
          <ChartTipMonth>{formatMonthFull(shown.month)}</ChartTipMonth>
          <ChartTipRow>
            <ChartTipName><ChartTipSwatch $color={recColor} />Recurring</ChartTipName>
            <ChartTipAmt>{fmtCurrency(shown.recurring)}</ChartTipAmt>
          </ChartTipRow>
          <ChartTipRow>
            <ChartTipName><ChartTipSwatch $color={TREND_OT_COLOR} />One-Time</ChartTipName>
            <ChartTipAmt>{fmtCurrency(shown.oneTime)}</ChartTipAmt>
          </ChartTipRow>
          {/* [SALES-PIA 2026-09-18, user] Listed between One-Time and Refunds / Disputes, the same
              order as the snapshot tiles. */}
          <ChartTipRow>
            <ChartTipName><ChartTipSwatch $color={TREND_PIA_COLOR} />Sales PIA</ChartTipName>
            <ChartTipAmt>{curPia == null ? '—' : fmtCurrency(curPia)}</ChartTipAmt>
          </ChartTipRow>
          <ChartTipRow>
            <ChartTipName><ChartTipSwatch $color={TREND_RD_COLOR} />Refunds / Disputes</ChartTipName>
            <ChartTipAmt $tone="red">{curRd == null ? '—' : fmtCurrency(curRd)}</ChartTipAmt>
          </ChartTipRow>
          {curWon != null && curWon > 0 && (
            <ChartTipRow>
              <ChartTipName><ChartTipSwatch $color="transparent" />Disputes Won</ChartTipName>
              <ChartTipAmt $tone="green">{fmtCurrency(curWon)}</ChartTipAmt>
            </ChartTipRow>
          )}
          <ChartTipDivider />
          <ChartTipRow $strong>
            <span>Total New Sales</span>
            <ChartTipAmt>{fmtCurrency(curTotal)}</ChartTipAmt>
          </ChartTipRow>
          <ChartTipRow $strong>
            <span>Net Sales</span>
            <ChartTipAmt $tone={curNet != null && curNet < 0 ? 'red' : undefined}>{curNet == null ? '—' : fmtCurrency(curNet)}</ChartTipAmt>
          </ChartTipRow>
        </ChartTip>
        </ChartTipPos>,
        document.body,
      )}
      <MonthRow>
        {data.map((d, i) => (
          <MonthLabel key={d.month} style={{ left: `${x(i)}%` }}>{formatMonthFull(d.month)}</MonthLabel>
        ))}
      </MonthRow>
    </>
  );
};

// Skeleton shown in the chart area while loading — faint lines + month-label placeholders so it
// reads as "the chart is loading". [SALES-PIA 2026-09-18] Four lines now, like the chart.
const ChartSkeleton: React.FC = () => (
  <>
    <ChartArea>
      <SkeletonBox $w="100%" $h="3px" $radius="2px" style={{ position: 'absolute', left: 0, top: '28%' }} />
      <SkeletonBox $w="100%" $h="3px" $radius="2px" style={{ position: 'absolute', left: 0, top: '48%' }} />
      <SkeletonBox $w="100%" $h="3px" $radius="2px" style={{ position: 'absolute', left: 0, top: '66%' }} />
      <SkeletonBox $w="100%" $h="3px" $radius="2px" style={{ position: 'absolute', left: 0, top: '82%' }} />
    </ChartArea>
    <MonthRow>
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonBox
          key={i}
          $w="70px"
          $h="11px"
          $radius="4px"
          style={{ position: 'absolute', left: `${5 + (i / 5) * 90}%`, transform: 'translateX(-50%)' }}
        />
      ))}
    </MonthRow>
  </>
);

// [MOBILE 2026-07-07] On phones the 6 months + their value labels crush together at
// full device width, so the chart gets a comfortable min-width (ChartInner) and scrolls
// horizontally inside this wrapper — the ChartArea and the month row live in the same
// inner block, so they scroll together and stay column-aligned. Desktop is untouched:
// the media query is the ONLY styling, so above 768px these are plain pass-through divs.
const ChartScroll = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    overscroll-behavior-x: contain; /* [PHONE-LAYOUT 2026-10-22] swipe stays in the chart */
  }
`;

const ChartInner = styled.div`
  @media (max-width: 768px) {
    min-width: 560px;
  }
`;

/* ── toolbar ── */

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 6px;
  border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.07)')};

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
    padding: 12px 0; /* [PHONE-LAYOUT 2026-10-22] the search row lines up with the cards above (FrozenTop gives 12px) */
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the page title, centred at the top (PhoneHead). */
  @media (max-width: 768px) {
    text-align: center;
    font-size: 17px;
    font-weight: 800;
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  /* [MOBILE 2026-07-07] Phones: the two dropdowns (Month + Sales Rep) share ONE row as
     an equal 2-column grid on their own line below the search+count row; the record
     count moves up into the search row (rendered only on desktop inside Filters). The
     glass triggers get min-width:0 so both shrink below their desktop floor and fit the
     phone width. Mirrors New Sales / Refunds. */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 8px;
    flex-wrap: nowrap;
    & > button { min-width: 0; width: 100%; }
  }
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

// [MOBILE 2026-07-07] Phones only: the search field and the record count share one row
// (count pinned to the right by the inline search's flex growth), with the two filter
// dropdowns on the row below. Desktop keeps search inline + count beside the filters.
// Mirrors New Sales.
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [MY-SALES PHONE PAGE SCROLL 2026-09-23, user] "the complete page will be scrollable vertically".
     On phones the stacked top band + toolbar are already taller than the screen, so flex: 1 +
     min-height: 0 squeezed this box to 0px of rows: the page (Container, overflow-y auto) scrolled
     only as far as the toolbar and the deals table could never be reached. Phones: take the rows'
     natural height so Container scrolls the whole page as one piece. overflow-x stays auto so the
     wide grid still scrolls sideways. Trade-off: the sticky header pins to THIS box, which no longer
     scrolls vertically, so on phones the header scrolls away with the page. Desktop unchanged. */
  @media (max-width: 768px) {
    flex: none;
    overflow-y: hidden;
    overscroll-behavior-x: contain; /* [PHONE-LAYOUT 2026-10-22] the deals table swipes in its own box */
    -webkit-overflow-scrolling: touch;
  }
`;


// [NO-CUT 2026-09-17, user] Company URL / Client Email show in full on one line; the column
// widens to fit (was capped at 170px with an ellipsis, which cut emails).
const NoWrap = styled.span`
  white-space: nowrap;
`;

/* [DEACTIVATED-OWNER 2026-07-30] Deal Owner cell for a rep who has left HubSpot. The name
   renders normally; this amber pill carries the state, the way HubSpot's own UI appends
   "(Deactivated)". Amber (not red) because it is information, not an error — nothing is
   wrong with the deal. Both themes are set explicitly: a dark-only colour would be
   unreadable on the light theme. Wraps under a long name instead of widening the column. */
const OwnerCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  row-gap: 2px;
`;

const InactiveTag = styled.span`
  flex: none;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  line-height: 1.5;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fbbf24' : '#b45309')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.13)' : 'rgba(180,83,9,0.09)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.30)' : 'rgba(180,83,9,0.22)')};
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-22] was 9.5px */
`;

/* ── component ── */

const MySalesPage: React.FC = () => {
  const { user } = useAuth(); // [COL-RESIZE-ALL 2026-09-29]
  const isMobile = useIsMobile(); // [MOBILE 2026-07-07] toolbar reflow + KPI/chart carousels + desktop table on phones
  const [data, setData] = useState<MySalesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // [MULTI-MONTH 2026-07-15] Multi-select month filter. Named selectedMonths, NOT
  // `months` — that name is already taken below by the SELECTABLE list from the API
  // (data.months), and shadowing it would gut the dropdown's options.
  // `monthsKey` is the stable effect dep (an array is a new identity each render).
  const [selectedMonths, setSelectedMonths] = useState<string[]>([currentMonth()]);
  const monthsKey = selectedMonths.join(',');
  const [salesPerson, setSalesPerson] = useState('');
  // [TOGGLE 2026-07-17] Right-pane view: 'leads' = the full leads table; 'overall' = the
  // Sales / Conversion% / In Bank / ATV columns blanked (Overall Sales content TBD). Default 'leads'.
  const [perfView, setPerfView] = useState<'leads' | 'overall'>('leads');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortConfig>({ sortBy: 'closeDate', sortOrder: 'desc' });
  // Tags each load so a slow earlier response can't overwrite a newer one.
  const reqIdRef = useRef(0);
  // When the server clamps salesPerson to the user's access scope, we adopt it
  // WITHOUT a redundant re-fetch (the just-loaded data already reflects it).
  const skipNextLoadRef = useRef(false);

  const load = useCallback(async (silent = false) => {
    const reqId = ++reqIdRef.current;
    // [MYSALES-FAST 2026-07-06] Seed instantly from the app's SWR GET cache so a month/
    // person combo we've already fetched (or the idle prefetch warmed) renders at once —
    // chart, KPIs and table together — with NO skeleton flash. Only a genuinely cold combo
    // flips the skeleton on. The background fetch below still runs and revalidates. The
    // peek mirrors fetchMySales's params exactly (undefined/'' dropped by the cache-key
    // normaliser) so the key lines up.
    const seed = peekCache<MySalesData>('/my-sales', monthsParams({ months: selectedMonths.length ? selectedMonths : undefined, salesPerson: salesPerson || undefined }));
    if (!silent) {
      if (seed) { setData(seed); setLoading(false); }
      else setLoading(true);
    }
    setError(null);
    try {
      const d = await fetchMySales(selectedMonths.length ? selectedMonths : undefined, salesPerson || undefined);
      if (reqId !== reqIdRef.current) return;
      setData(d);
      // Adopt the access-scoped person (e.g. a rep's '' → their own name) so the
      // dropdown + heading match the data shown — no re-fetch (data already reflects it).
      if (d.salesPerson !== salesPerson) {
        skipNextLoadRef.current = true;
        setSalesPerson(d.salesPerson);
      }
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      // Keep a good seed on screen rather than flashing an error over it.
      if (!seed) setError(e?.response?.data?.error || 'Failed to load My Sales.');
    } finally {
      // Clear the skeleton when THIS is the latest request — silent OR not. Dropping the
      // old `!silent` guard fixes a stuck-skeleton race on the FIRST (cold) visit: if a
      // silent realtime refresh (refunds/disputes tick) fired DURING the slow initial
      // load, it bumped reqId so the initial load bailed above without clearing loading,
      // and the silent load — being `!silent`-guarded here — never cleared it either →
      // skeleton stuck until a manual refresh. The reqId===current gate still upholds the
      // skeleton-flash invariant (only the winning request hides the skeleton, and it does
      // so over its own freshly-set data). Silent loads still never turn the skeleton ON.
      if (reqId === reqIdRef.current) setLoading(false);
    }
    // monthsKey, not an array — a fresh identity each render would loop forever.
  }, [monthsKey, salesPerson]);

  useEffect(() => {
    if (skipNextLoadRef.current) { skipNextLoadRef.current = false; return; }
    load();
  }, [load]);
  // [MYSALES-FRESH 2026-09-18, user] One subscription for the whole tab: the server's `mySales`
  // version covers deals (a new sale shows within seconds of reaching HubSpot), refunds, disputes,
  // payments and inventory. It REPLACES ['refunds','disputes'] — those bumped alongside `mySales`
  // for the same change, so the page reloaded twice. Silent, so live updates never flash a spinner.
  useRealtime(['mySales'], () => load(true));

  // [DB-OUTAGE-FIX 2026-10-28, user] A half-loaded leads table (its Leads_Raw or deals leg failed → "—")
  // asks again by itself every 10 s, quietly (no skeleton), paused while the browser tab is hidden — the
  // same rule as the My Inventory KPI strip. It used to wait for the next live update.
  const perfPartial = !loading && !!data?.leadsPerf
    && (data.leadsPerf.leadsOk === false || data.leadsPerf.overallOk === false);
  useEffect(() => {
    if (!perfPartial) return undefined;
    const id = window.setInterval(() => { if (!document.hidden) load(true); }, 10000);
    return () => window.clearInterval(id);
  }, [perfPartial, load]);

  const deals = useMemo(() => data?.deals ?? [], [data]);
  const summary = data?.summary;
  const refundsDisputesItems = data?.refundsDisputesBreakdown ?? [];
  const disputesWonItems = data?.disputesWonBreakdown ?? [];
  const months = data?.months ?? [];
  const trend = data?.trend ?? [];
  const salesPeople = data?.salesPeople ?? [];
  const canSelectAll = data?.canSelectAll ?? false;   // admin/senior only — show the "All Sales" option
  // [MULTI-MONTH 2026-07-15] Union the ticked months with the API's selectable list
  // so a selected month is never missing from the options, then newest-first.
  const monthOptions = Array.from(new Set([...selectedMonths, ...months])).sort((a, b) => b.localeCompare(a));
  // [SEARCH-ALL 2026-08-31] Every selectable month, newest-first — what "All Months" means.
  const allMonths = monthOptions;
  const isAllMonths = allMonths.length > 0 && selectedMonths.length === allMonths.length;
  // [MULTI-MONTH 2026-07-15] Human label for the headings — "July 2026" for one
  // month, "3 months" for several (matches the dropdown trigger's wording).
  const monthLabel = selectedMonths.length === 1
    ? formatMonthFull(selectedMonths[0])
    : isAllMonths ? 'All months' : `${selectedMonths.length} months`;
  // Keep the selected salesperson in the dropdown even in a month where they have
  // no deals, so changing the month never silently resets the name — the heading
  // and the dropdown stay in sync (the selection persists across months).
  const salesPeopleOptions = salesPerson && !salesPeople.includes(salesPerson)
    ? [...salesPeople, salesPerson].sort((a, b) => a.localeCompare(b))
    : salesPeople;

  /* [SEARCH-ALL 2026-08-31, user] A search WIDENS the filters instead of quietly ignoring them.
     Before this, typing a term searched the whole allowed scope but only the LOADED month, while
     both dropdowns still read "July 2026" / "Anshika Mishra" — so the screen claimed a filter it
     was not applying and hid months it never looked at. Now the dropdowns move to match:
       • month  -> All Months, for everyone. This is what makes the result COMPLETE; the server
         already handles a multi-month selection as ONE cached span fetch, so it costs one
         HubSpot call, not one per month.
       • person -> All Sales Rep, but ONLY for admins/seniors who may select it (user: "for the
         individual person wise when they make the search then they will see only his data").
         A rep's payload never contains anyone else, so their search is self-scoped by the
         server, not by this dropdown — clearing it for them would just be clamped straight back.
     All Months is a SEARCH-ONLY state (user: "month dropdown will be set to current month and
     when someone search then only it will show all month and then it will be on the current
     month"): the dropdown opens on the current month, only a live search puts it on All Months,
     and clearing the search returns it there. The person, which the user may have picked
     deliberately, is restored to whatever it was instead. */
  const preSearchRef = useRef<string | null>(null);
  const handleSearch = (next: string) => {
    const had = search.trim().length > 0;
    const has = next.trim().length > 0;
    if (has && !had) {
      preSearchRef.current = salesPerson;
      if (allMonths.length) setSelectedMonths(allMonths);
      if (canSelectAll) setSalesPerson('');
    } else if (!has && had) {
      const prevPerson = preSearchRef.current;
      preSearchRef.current = null;
      setSelectedMonths([currentMonth()]);
      if (canSelectAll && prevPerson != null) setSalesPerson(prevPerson);
    }
    setSearch(next);
  };

  const handleSort = (col: string) => {
    setSort((prev) => ({
      sortBy: col,
      sortOrder: prev.sortBy === col && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Search + sort over the loaded deals (client-side — the fetch returns the
  // whole month, so filtering/sorting never refetch).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = deals;   // the user's full allowed scope (rep = self, lead = team, senior = all)
    if (q) {
      // Search spans the WHOLE allowed scope, regardless of the selected person.
      rows = rows.filter((d) =>
        d.dealName.toLowerCase().includes(q) ||
        d.dealOwner.toLowerCase().includes(q) ||
        d.email.toLowerCase().includes(q) ||
        d.companyUrl.toLowerCase().includes(q));
    } else if (salesPerson) {
      // No search → the table shows just the selected person.
      rows = rows.filter((d) => d.dealOwner === salesPerson);
    }
    if (!sort.sortBy) return rows;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    const k = sort.sortBy as keyof MySalesDeal;
    return [...rows].sort((a, b) => {
      const av = a[k];
      const bv = b[k];
      if (typeof av === 'number' || typeof bv === 'number') {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [deals, search, sort, salesPerson]);

  const columns: Column[] = useMemo(() => [
    { key: 'dealName', label: 'Deal Name', sortable: true, width: '190px', render: (v: any) => v || '—' },
    // [DEACTIVATED-OWNER 2026-07-30] A departed rep's name now resolves (server reads the
    // archived HubSpot owners) and is tagged, so the cell is no longer blank and nobody has
    // to guess why. `title` spells it out in full on hover for screen readers/tooltips.
    { key: 'dealOwner', label: 'Deal Owner', sortable: true, width: '160px',
      render: (v: any, row: any) => (!v ? '—' : row?.ownerInactive ? (
        <OwnerCell title={`${v} (Deactivated) — no longer in HubSpot`}>
          {v}<InactiveTag>Deactivated</InactiveTag>
        </OwnerCell>
      ) : v) },
    // [TEAM-NAME 2026-09-18, user] The Sales team the deal owner belongs to (server).
    { key: 'teamName', label: 'Team Name', sortable: true, width: '150px', render: (v: any) => v || '—' },
    { key: 'paymentType', label: 'Payment Type', sortable: true, align: 'center', width: '140px', render: (v: any) => paymentTypeLabel(v) },
    { key: 'pipeline', label: 'Pipeline', sortable: true, align: 'center', width: '130px', render: (v: any) => v || '—' },
    { key: 'companyUrl', label: 'Company URL', sortable: true, width: '180px', headerAlign: 'left',
      render: (v: any) => (v ? <NoWrap>{v}</NoWrap> : '—') },
    { key: 'email', label: 'Client Email', sortable: true, width: '190px',
      render: (v: any) => (v ? <NoWrap>{v}</NoWrap> : '—') },
    { key: 'amount', label: 'Amount Received', sortable: true, align: 'center', width: '140px', render: money },
    // [SECOND-MONTH 2026-09-17, user] Recurring deals only — One-Time shows "—". The value is the
    // deal's share (by amount) of its website's Retention in the next month's inventory (server);
    // null = that month has not started → "—".
    { key: 'secondMonthPayment', label: 'Second Month Payment Received', sortable: true, align: 'center', width: '240px',
      render: (v: any, row: any) => (row?.paymentType !== 'Reoccurring' || v == null ? '—' : money(v)) },
    // [NO-DEPT-COLUMNS 2026-09-17, user] Creative / Ranking / Performance / Tech Amount columns
    // removed from this table ("of no use"). The server still sends the fields; nothing reads them here.
  ], []);

  const salesLabel = salesPerson || 'All Sales Rep';
  // [TOGGLE 2026-07-17] Overall Sales view blanks the sales-side columns of the leads table.
  const overall = perfView === 'overall';

  // [MYSALES-REPORT 2026-10-28, user] Download: the data is asked for the moment the menu opens (so a pick is quick) and
  // is used for the pick made in that menu; a failed ask is never reused — the pick asks again. The server decides who is
  // in it (admin / senior = every active rep, team lead = his team, rep = himself) and measures it at that moment.
  const reportRef = useRef<Promise<MySalesReport> | null>(null);
  const startReport = useCallback(() => {
    const ask = fetchMySalesReport();
    reportRef.current = ask;
    ask.catch(() => { if (reportRef.current === ask) reportRef.current = null; });
  }, []);
  // [MYSALES-REPORT-PICK 2026-10-28, user] "a pop-up in the middle of the page with the names … select whatever I want in
  // the report": after Excel / PDF is picked, the reps the file can carry are listed (ReportPeopleDialog) and only the
  // ticked ones go in. The reps left out last time start unticked (kept in this browser, per signed-in user).
  const [pick, setPick] = useState<{ format: 'xlsx' | 'pdf'; report: MySalesReport; names: string[]; initial: string[] } | null>(null);
  const [pickBusy, setPickBusy] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const leftOutKey = `dt-my-sales-report-left-out:${user?.id ?? 'anon'}`;
  const readLeftOut = useCallback((): string[] => {
    try {
      const v = JSON.parse(localStorage.getItem(leftOutKey) || '[]');
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch { return []; }
  }, [leftOutKey]);
  const onDownloadPick = useCallback(async (key: string): Promise<string | void> => {
    if (!reportRef.current) startReport();
    let report: MySalesReport;
    try {
      report = await reportRef.current!;
    } catch (e: any) {
      throw new Error(e?.response?.data?.error || 'Could not get the sales data. Please try again.');
    }
    const format = key === 'pdf' ? 'pdf' : 'xlsx';
    const names = reportPeople(report);
    if (!names.length) return 'No sales rep has data in these 6 months, so there is nothing to download.';
    // A rep's own file (one name): nothing to choose, straight to the file.
    if (names.length === 1) { downloadMySalesReport(report, format); return; }
    const left = new Set(readLeftOut());
    setPickError(null);
    setPick({ format, report, names, initial: names.filter((n) => !left.has(n)) });
  }, [startReport, readLeftOut]);
  const onPickCancel = useCallback(() => setPick(null), []);
  const onPickConfirm = useCallback(async (selected: string[]) => {
    const p = pick;
    if (!p) return;
    setPickBusy(true);
    setPickError(null);
    await new Promise((r) => setTimeout(r, 30));   // let "Preparing…" show before the file is made
    try {
      downloadMySalesReport(p.report, p.format, selected);
      try {
        const keep = readLeftOut().filter((n) => !p.names.includes(n));
        localStorage.setItem(leftOutKey, JSON.stringify([...keep, ...p.names.filter((n) => !selected.includes(n))]));
      } catch { /* a browser that keeps nothing: every pop-up starts all ticked */ }
      setPick(null);
    } catch (e: any) {
      setPickError(e?.message || 'Could not make the file. Please try again.');
    } finally {
      setPickBusy(false);
    }
  }, [pick, readLeftOut, leftOutKey]);
  const perfMonths = data?.leadsPerf?.rows ?? [];
  const downloadTitle = perfMonths.length
    ? `Download · ${fmtPeriodShort(perfMonths[0].month, perfMonths[perfMonths.length - 1].month)}`
    : 'Download';

  const perfHead = (chunks: string[]) => chunks.map((c, i) => (
    <React.Fragment key={c}>{i > 0 && ' '}<PerfHeadChunk>{c}</PerfHeadChunk></React.Fragment>
  ));

  // [VIEW-SLIDE 2026-10-08, user] The right-pane table for ONE view (`ov` = Overall Sales), so both views can be on
  // screen while the toggle slides between them. Same figures, columns and formats as before — the per-view values
  // below were page-level consts reading the toggle; now they read `ov`.
  const perfTable = (lp: MySalesLeadsPerf, ov: boolean) => {
    // [SILENT-PARTIAL FIX 2026-08-15] The 'Overall Sales' columns are HubSpot-derived; when that
    // leg fails every `overall.*` value is a default 0, which would read as a real measurement.
    // Suppress those four columns to "—" instead.
    // [DB-OUTAGE-FIX 2026-10-28, user] The DB-backed side has its own flag now (leadsOk): when the Leads_Raw
    // query failed, the Leads column (both views) and the whole 'Leads to Sales' view are unknown, not 0 —
    // on 2026-10-28 a slow DB showed "0 leads, $0.00" for every month. Conversion% divides by leads, so it
    // is "—" in both views then. An older server sends no flag = healthy.
    const leadsSuppressed = lp.leadsOk === false;
    const perfSuppressed = ov ? lp.overallOk === false : leadsSuppressed;
    const perfInt = (v: number) => (perfSuppressed ? '—' : fmtInt(v));
    const perfPct = (v: number) => (perfSuppressed || leadsSuppressed ? '—' : fmtPct(v));
    const perfLeads = (v: number) => (leadsSuppressed ? '—' : fmtInt(v));
    // [OVERALL-BREAKDOWN 2026-09-29, user] Whole dollars in the Overall Sales view; Leads to Sales keeps cents.
    const perfMoney = (v: number) => (perfSuppressed ? '—' : ov ? fmtCurrencyWhole(v) : fmtCurrency(v));

    // [OVERALL-BREAKDOWN 2026-09-29, user] The Net In-Bank parts this view shows (PERF_PARTS): an optional
    // one only when the rep has an amount in it in one of the 6 months — a hidden column is $0 in every
    // month, so each row still adds up. Unmeasured figures (perfSuppressed) show all five as "—": an
    // unknown is not the same as none. An older server sends no parts: those cells read "—", never $0.
    const perfParts = ov
      ? PERF_PARTS.filter((p) => p.always || perfSuppressed
          || (lp.rows ?? []).some((r) => Math.abs(r.overall[p.key] ?? 0) >= 0.005))
      : [];
    // Net In-Bank + ATV are money columns too, hence the + 2.
    const perfMinWidth = PERF_W.month + PERF_W.leads + PERF_W.sales + PERF_W.conversion + PERF_W.money * (perfParts.length + 2);
    const perfColPct = (w: number) => `${((w / perfMinWidth) * 100).toFixed(3)}%`;
    const perfPart = (v: number | undefined, tone?: 'red' | 'green') =>
      perfSuppressed || v === undefined ? '—'
        : tone ? <PerfAmt $tone={tone}>{fmtCurrencyWhole(v)}</PerfAmt>
        : fmtCurrencyWhole(v);

    return (
      <PerfScroll>
        {/* [OVERALL-BREAKDOWN 2026-09-29, user] Overall view: wide layout + proportional columns. */}
        <PerfTable $wide={ov} $minWidth={ov ? perfMinWidth : undefined}>
          {ov && (
            <colgroup>
              <col style={{ width: perfColPct(PERF_W.month) }} />
              <col style={{ width: perfColPct(PERF_W.leads) }} />
              <col style={{ width: perfColPct(PERF_W.sales) }} />
              <col style={{ width: perfColPct(PERF_W.conversion) }} />
              {perfParts.map((p) => <col key={p.key} style={{ width: perfColPct(PERF_W.money) }} />)}
              <col style={{ width: perfColPct(PERF_W.money) }} />
              <col style={{ width: perfColPct(PERF_W.money) }} />
            </colgroup>
          )}
          <thead>
            <tr>
              <th>Month</th>
              <th>Leads</th>
              <th>Sales</th>
              <th>Conversion%</th>
              {perfParts.map((p) => <th key={p.key}>{perfHead(p.head)}</th>)}
              {/* [NET-IN-BANK 2026-09-16, user] Only the Overall Sales view is net, so only it says so. */}
              <th>{ov ? perfHead(['Net', 'In-Bank']) : 'In Bank'}</th>
              <th>ATV</th>
            </tr>
          </thead>
          <tbody>
            {lp.rows.map((r) => (
              <tr key={r.month}>
                <td>{fmtMonthYY(r.month)}</td>
                <td>{perfLeads(r.leads)}</td>
                <td>{perfInt((ov ? r.overall : r).sales)}</td>
                <td>{perfPct((ov ? r.overall : r).conversion)}</td>
                {perfParts.map((p) => <td key={p.key}>{perfPart(r.overall[p.key], p.tone)}</td>)}
                <td>{perfMoney((ov ? r.overall : r).inBank)}</td>
                <td>{perfMoney((ov ? r.overall : r).atv)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td>{perfLeads(lp.total.leads)}</td>
              <td>{perfInt((ov ? lp.total.overall : lp.total).sales)}</td>
              <td>{perfPct((ov ? lp.total.overall : lp.total).conversion)}</td>
              {perfParts.map((p) => <td key={p.key}>{perfPart(lp.total.overall[p.key], p.tone)}</td>)}
              <td>{perfMoney((ov ? lp.total.overall : lp.total).inBank)}</td>
              <td>{perfMoney((ov ? lp.total.overall : lp.total).atv)}</td>
            </tr>
          </tfoot>
        </PerfTable>
      </PerfScroll>
    );
  };

  // KPI value with a skeleton while loading (covers the initial load AND dropdown
  // changes), so the snapshot reads as "loading" instead of showing a stale value.
  const kpiValue = (v: number | undefined) =>
    loading ? <SkeletonBox $w="96px" $h="17px" $radius="6px" /> : v !== undefined ? fmtCurrency(v) : '—';

  // [SILENT-PARTIAL FIX 2026-08-15] The HubSpot deal search is paged and shares one
  // rate-limited token with every background job. When the server cannot complete it, the
  // deal-derived figures are UNKNOWN — not zero. Rendering $0.00 there is what made a
  // transient HubSpot failure look like "this rep sold nothing all month". Absent field (an
  // older server build) counts as healthy, so this can only ever add a warning, never remove data.
  const dealsHealth = data?.dealsHealth;
  const dealsOk = dealsHealth ? dealsHealth.ok : true;
  const dealsStale = !!dealsHealth?.stale;

  // Deal-derived KPI: shows "—" instead of a fabricated $0.00 when the fetch failed. The
  // Refunds/Disputes + Disputes Won tiles below are DB-backed and stay accurate regardless.
  const dealKpiValue = (v: number | undefined) =>
    loading ? <SkeletonBox $w="96px" $h="17px" $radius="6px" /> : dealsOk ? kpiValue(v) : '—';

  // [NET-SALES 2026-09-16, user] Net Sales = Total New Sales − Refunds / Disputes + Disputes Won,
  // from the same three figures the tiles beside it show. Total New Sales is deal-derived, so the
  // tile goes "—" with it (dealKpiValue) when HubSpot could not be read. Rounded to cents.
  // [SALES-PIA 2026-09-18, user] "net sales will be total new sales plus sales pia plus dispute won
  // minus refund/dispute" — four tiles now, all shown beside it. `?? 0` covers the seconds during a
  // deploy when an older server sends no salesPia; it can only ever omit PIA, never invent it.
  const netSales = summary
    ? Math.round((summary.totalNewSales + (summary.salesPia ?? 0) - summary.refundsDisputes + summary.disputesWon) * 100) / 100
    : undefined;
  const netSalesNeg = netSales !== undefined && netSales < 0;

  // [SPLIT-VIEW 2026-07-15] Built once so the desktop (ScaleToFit-wrapped) and phone
  // (bare carousel) branches render the SAME tiles + hover handlers.
  const kpiTiles = (
    <KpiRow>
      <Kpi><KpiLabel>Recurring Sales</KpiLabel><KpiValue $tone="green">{dealKpiValue(summary?.recurringSales)}</KpiValue></Kpi>
      <Kpi><KpiLabel>One-Time Sales</KpiLabel><KpiValue $tone="green">{dealKpiValue(summary?.oneTimeSales)}</KpiValue></Kpi>
      <Kpi><KpiLabel>Total New Sales</KpiLabel><KpiValue $tone="strong">{dealKpiValue(summary?.totalNewSales)}</KpiValue></Kpi>
      {/* [SALES-PIA 2026-09-18, user] Straight after Total New Sales, and deliberately not inside it. */}
      <Kpi><KpiLabel>Sales PIA</KpiLabel><KpiValue $tone="green">{dealKpiValue(summary?.salesPia)}</KpiValue></Kpi>
      <BreakdownKpi
        label="Refunds / Disputes"
        tone="red"
        loading={loading}
        value={summary?.refundsDisputes}
        items={refundsDisputesItems}
        countLabel={(n) => `${n} refund${n !== 1 ? 's' : ''} / dispute${n !== 1 ? 's' : ''}`}
      />
      <BreakdownKpi
        label="Disputes Won"
        tone="green"
        loading={loading}
        value={summary?.disputesWon}
        items={disputesWonItems}
        countLabel={(n) => `${n} dispute${n !== 1 ? 's' : ''} won`}
        showType={false}
      />
      <Kpi>
        <KpiLabel>Net Sales</KpiLabel>
        <KpiValue $tone={netSalesNeg ? 'red' : 'strong'}>
          {dealKpiValue(netSales)}
        </KpiValue>
      </Kpi>
    </KpiRow>
  );

  // [PHONE-LAYOUT 2026-10-22] The Month + Sales Rep pickers, built ONCE: the laptop keeps them in the toolbar exactly
  // as before; phones show them in the page head at the top (PhoneHead).
  const monthSelect = (
            /* [SINGLE-MONTH 2026-07-28] One month at a time (multi-select withdrawn).
                State stays a string[] of length 1 so nothing downstream changes.
                [SEARCH-ALL 2026-08-31] ...except while a search is running, when it reads
                "All Months". That option is rendered ONLY then — the same trick the Payments
                tab uses for its own search-all-months sentinel: with no matching option the
                trigger would fall back to rendering the FIRST one, so the control would claim
                a single month while the results spanned every one of them. */
            <GlassSelect liquid
              deferChange /* [FLUID-8 2026-10-12] instant pick */
              value={isAllMonths ? '' : (selectedMonths[0] || '')}
              onChange={(e) => {
                setSearch('');
                preSearchRef.current = null;
                setSelectedMonths([e.target.value || currentMonth()]);
              }}
            >
              {isAllMonths && <option value="">All Months</option>}
              {monthOptions.map((m) => (
                <option key={m} value={m}>{formatMonthFull(m)}</option>
              ))}
            </GlassSelect>
  );
  const repSelect = (
            <GlassSelect liquid
              value={salesPerson}
              deferChange /* [FLUID-8 2026-10-12] instant pick */ onChange={(e) => { setSearch(''); preSearchRef.current = null; setSalesPerson(e.target.value); }}
            >
              {/* Show "All Sales Rep" for admins/seniors, AND during the very first
                  load (data not in yet) so the dropdown keeps its width instead of
                  collapsing to an empty, thin box. */}
              {(canSelectAll || !data) && <option value="">All Sales Rep</option>}
              {salesPeopleOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </GlassSelect>
  );

  // [PHONE-LAYOUT 2026-10-22] Phones: the 6-month chart is wider than the screen (its approved sideways box), so it
  // opens on the NEWEST months (scrolled to its right end); a swipe back shows the older ones. Runs again only when
  // the chart's months, the rep or the loading state change (not on a live refresh), and only on phones.
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const trendMonthsKey = trend.map((t) => t.month).join(',');
  useLayoutEffect(() => {
    const el = chartScrollRef.current;
    if (isMobile && el) el.scrollLeft = el.scrollWidth;
  }, [isMobile, loading, dealsOk, trendMonthsKey, salesPerson]);

  return (
    <Container>
      {/* [PHONE-LAYOUT 2026-10-22] Phones: the page head on top: the title, then the Month + Sales Rep pickers that set
          every part of the page (they sat mid-page, a full screen down). The laptop renders nothing here. */}
      {isMobile && (
        <PhoneHead>
          <PageTitle>My Sales</PageTitle>
          <Filters>{monthSelect}{repSelect}</Filters>
        </PhoneHead>
      )}
      {/* [SPLIT-VIEW 2026-07-15] Split band: chart + KPIs left, new table right. */}
      <SplitTop>
        <SplitLeft>
        <PaneHeading $center>Sales | <HeadingScope>All Recurring and One-Time Sales</HeadingScope></PaneHeading>
        <TrendStrip>
          <TrendHead>
            <TrendTitleWrap>
              <TrendTitle>Last 6 Months</TrendTitle>
              <TrendMeta>{salesLabel}</TrendMeta>
            </TrendTitleWrap>
            <Legend>
              <LegendItem><Swatch $color={TREND_REC_COLOR} />Recurring</LegendItem>
              <LegendItem><Swatch $color={TREND_OT_COLOR} />One-Time</LegendItem>
              {/* [CHART REFUNDS 2026-09-17] Always listed, so the legend does not jump when data lands. */}
              <LegendItem><Swatch $color={TREND_PIA_COLOR} />Sales PIA</LegendItem>
              <LegendItem><Swatch $color={TREND_RD_COLOR} />Refunds / Disputes</LegendItem>
            </Legend>
          </TrendHead>
          {/* [MOBILE 2026-07-07] Wrap the chart so phones can scroll it horizontally at
              a comfortable width instead of crushing 6 months of labels together.
              Above 768px the wrappers are inert (no styling), so desktop is unchanged. */}
          <ChartScroll ref={isMobile ? chartScrollRef : undefined /* [PHONE-LAYOUT 2026-10-22] phones open on the newest month */}>
            <ChartInner>
              {loading ? <ChartSkeleton />
                : dealsOk ? <TrendChart data={trend} />
                : <ChartUnavailable>Trend unavailable — HubSpot sales data could not be loaded.</ChartUnavailable>}
            </ChartInner>
          </ChartScroll>
        </TrendStrip>

        <Snapshot>
          <SnapHead>
            <SnapTitle>Sales Snapshot</SnapTitle>
            <SnapMeta>{salesLabel} · {monthLabel}</SnapMeta>
          </SnapHead>
          {/* [SILENT-PARTIAL FIX 2026-08-15] Never let a HubSpot outage read as "$0 sold".
              Only rendered once loading has settled, so it can't flash during a refetch. */}
          {!loading && !dealsOk && (
            <DealsWarn $tone="warn" role="status">
              <span aria-hidden="true">⚠</span>
              <span>
                {dealsHealth?.message || 'Live sales data from HubSpot is temporarily unavailable.'}
                {' '}Refunds / Disputes and the Leads to Sales table are unaffected. This usually
                clears on its own within a minute — reload to retry.
              </span>
            </DealsWarn>
          )}
          {!loading && dealsOk && dealsStale && (
            <DealsWarn $tone="info" role="status">
              <span aria-hidden="true">↻</span>
              <span>
                Showing the last successful HubSpot fetch
                {dealsHealth?.asOf ? ` from ${new Date(dealsHealth.asOf).toLocaleTimeString()}` : ''} — a
                refresh just failed, so very recent deals may be missing.
              </span>
            </DealsWarn>
          )}
          {/* [SPLIT-VIEW 2026-07-15] Desktop: the row lives in half the page now, so
              <ScaleToFit> shrinks all 7 tiles uniformly rather than letting them
              truncate. Phones: KpiRow is its own scroll-snap carousel (and ScaleToFit
              owns an overflow-x too — nesting them would fight), so mount it bare.
              Same tiles either way. Mirrors the Inventory Performance Snapshot.
              [SALES-PIA 2026-09-18, user] 7 since the Sales PIA tile — the count MUST match the
              tiles, or ScaleToFit scales to the wrong natural width and the row truncates again. */}
          {isMobile ? kpiTiles : (
            <ScaleToFit contentMinWidth={kpiRowMinWidth(7)}>{kpiTiles}</ScaleToFit>
          )}
        </Snapshot>
        </SplitLeft>

        {/* [SPLIT-VIEW 2026-07-15] RIGHT of the divider = reserved for the new
            table. Placeholder only — nothing is fetched or rendered here yet. */}
        <SplitRight>
          <PaneHeading $center $withToggle>
            {/* [MYSALES-REPORT 2026-10-28, user] Download (Excel / PDF) on the left, the title in the middle, the toggle on
                the right. Phones: the round icon, on the line under the title. */}
            <HeadDownload>
              <DownloadMenu compact={isMobile} title={downloadTitle} choices={DOWNLOAD_CHOICES} onOpen={startReport} onPick={onDownloadPick} />
            </HeadDownload>
            <HeadTitle>Leads to Sales | <HeadingScope>{salesLabel}</HeadingScope></HeadTitle>
            <ToggleWrap>
              {/* [FLUID-8 2026-10-12, user] InstantPick: the pill moves on the mouse PRESS; the board's switch follows. */}
              <InstantPick value={perfView} onPick={setPerfView}>
                {(shown, press) => (
                  <PerfToggle role="tablist" aria-label="Table view">
                    <PerfToggleKnob $right={shown === 'overall'} />
                    <PerfToggleOpt type="button" $active={shown !== 'overall'} aria-pressed={shown !== 'overall'} {...press('leads')}>Leads to Sales</PerfToggleOpt>
                    <PerfToggleOpt type="button" $active={shown === 'overall'} aria-pressed={shown === 'overall'} {...press('overall')}>Overall Sales</PerfToggleOpt>
                  </PerfToggle>
                )}
              </InstantPick>
            </ToggleWrap>
          </PaneHeading>
          {/* [SKELETON-ON-CHANGE 2026-07-20] Check `loading` FIRST (like the chart,
              KPIs and deals table on this page) so a cold rep/month change shows the
              skeleton instead of the PREVIOUS rep's table lingering while the fetch runs.
              A warm (already-fetched) combo seeds via peekCache → loading is set false
              immediately in load(), so there's no skeleton flash on revisit. */}
          {/* [VIEW-SLIDE 2026-10-08, user] Both views stay mounted and slide (skeleton too, while loading); Leads to
              Sales rests on the LEFT, Overall Sales on the RIGHT. The In Bank lines sit under both and never move. */}
          {loading || data?.leadsPerf ? (
            <PerfWrap aria-busy={loading || undefined} aria-label={loading ? 'Loading' : undefined}>
              <PerfViewport>
                {PERF_VIEWS.map((v) => (
                  <SlidePane key={v} $pos={slidePos(perfView, v, PERF_VIEWS)} aria-hidden={perfView !== v} data-mysales-pane={v}>
                    {loading || !data?.leadsPerf
                      ? <PerfSkeleton cols={v === 'overall' ? 8 : PERF_COLS} />
                      : perfTable(data.leadsPerf, v === 'overall')}
                  </SlidePane>
                ))}
              </PerfViewport>
              <InBankNotes />
            </PerfWrap>
          ) : (
            <RightPlaceholder>{error ? 'Not loaded yet. It will fill in once the data loads.' : 'No sales data available.' /* [LOAD-STATES 2026-09-29] */}</RightPlaceholder>
          )}
        </SplitRight>
      </SplitTop>

      {/* Full width from here down: toolbar frozen over the scrolling deals table. */}
      <FrozenTop>
        <Toolbar>
          {/* [PHONE-LAYOUT 2026-10-22] Phones: the title and the 2 pickers are in PhoneHead at the top; only the search
              row stays here, right above the deals it searches. Laptop: unchanged. */}
          {!isMobile && <PageTitle>My Sales</PageTitle>}
          {/* [MOBILE 2026-07-07] Phones: search + record count share one row (SearchRow),
              the 2 dropdowns drop to their own 2-column row (Filters grid). Desktop: search
              sits inline in the middle, count sits beside the filters. Mirrors New Sales. */}
          {isMobile ? (
            <SearchRow>
              <SearchBar inline liquid placeholder="Search sales..." value={search} onChange={handleSearch} />
              <RecordCount>{error && !data ? 'Not loaded' : `${filtered.length.toLocaleString()} records found`}</RecordCount>
            </SearchRow>
          ) : (
            <SearchBar inline liquid placeholder="Search sales..." value={search} onChange={handleSearch} />
          )}
          {/* [PHONE-LAYOUT 2026-10-22] Laptop only now (phones: PhoneHead); same count + pickers as before. */}
          {!isMobile && (
          <Filters>
            {!isMobile && <RecordCount>{error && !data ? 'Not loaded' : `${filtered.length.toLocaleString()} records found`}</RecordCount>}
            {monthSelect}
            {repSelect}
          </Filters>
          )}
        </Toolbar>
      </FrozenTop>

      <ScrollContainer>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : error ? (
          // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
          <LoadFailed what="sales" onRetry={() => load()} />
        ) : filtered.length === 0 ? (
          /* [SILENT-PARTIAL FIX 2026-08-15] "No sales found" is a CLAIM about the data. Only
             make it when we actually have the data — otherwise say we couldn't load it.
             [LOAD-STATES 2026-09-29, user] The HubSpot miss now retries by itself (countdown card). */
          !dealsOk
            ? <LoadFailed what="sales from HubSpot" onRetry={() => load()} />
            : search.trim()
              ? <EmptyState icon="search" title={`No sales match “${search.trim()}”`} hint="Check the spelling, or clear the search to see every sale." action={{ label: 'Clear search', onClick: () => handleSearch('') }} />
              : <EmptyState title={`No sales in ${monthLabel}`} hint="Pick another month or sales rep to see more." />
        ) : (
          <DataTable
            parentScroll
            columns={columns}
            data={filtered}
            sort={sort}
            onSort={handleSort}
            // [MOBILE 2026-07-07] Wide My Sales grid — keep the SAME desktop table on
            // phones (no card view); it scrolls horizontally inside ScrollContainer so
            // the full grid reads exactly like the website. Mirrors New Sales / Refunds.
            disableMobileCards
            // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
            resizeStorageKey={`dt-my-sales-colw:${user?.id ?? 'anon'}`}
            highlight={search} // [SEARCH-HIGHLIGHT 2026-09-29]
          />
        )}
      </ScrollContainer>
      {/* [MYSALES-REPORT-PICK 2026-10-28, user] Which sales reps go in the file — opens after Excel / PDF is picked. */}
      <ReportPeopleDialog
        open={!!pick}
        title={pick?.format === 'pdf' ? 'Download PDF' : 'Download Excel'}
        period={pick ? fmtPeriodShort(pick.report.months[0], pick.report.months[pick.report.months.length - 1]) : ''}
        icon={pick?.format === 'pdf' ? 'fa-solid fa-file-pdf' : 'fa-solid fa-file-excel'}
        tone={pick?.format === 'pdf' ? 'red' : 'green'}
        names={pick?.names ?? []}
        initialSelected={pick?.initial ?? []}
        busy={pickBusy}
        error={pickError}
        onCancel={onPickCancel}
        onConfirm={onPickConfirm}
      />
    </Container>
  );
};

export default MySalesPage;
